/* eslint-disable babel/camelcase */
import { handleActions, combineActions } from "redux-actions";
import moment from "moment";
import fromPairs from "lodash-es/fromPairs";
import some from "lodash-es/some";
import values from "lodash-es/values";
import sum from "lodash-es/sum";
import { configuration } from "../model/config";

// taken from https://emailregex.com/
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const formatAnalyticsDay = d => moment(d).format("DD-MM-YYYY");

const validateNotEmpty = str => (!str ? "Non può essere vuoto" : "");
const validateNotDefault = country =>
  country === countries[lang].default ? "Paese obbligatorio" : "";
const validateCap = str =>
  /^[0-9]+$/.test(str) ? "" : "Il CAP deve essere numerico";
const validatePhoneNumber = str =>
  /^[0-9 ]+$/.test(str) ? "" : "Numero di telefono non valido";
const validateEmail = str => (emailRegex.test(str) ? "" : "Email non valida");
const validateExperiencesDates = experiencesDates =>
  some(values(experiencesDates), x => !x)
    ? "Fornire una data per ogni esperienza"
    : "";
const validateExperiencesTimes = experiencesTimes =>
  some(values(experiencesTimes), x => !x)
    ? "Fornire un orario per ogni esperienza"
    : "";

import {
  setName,
  setSurname,
  setCountry,
  setPostcode,
  setEmail,
  setPhoneNumber,
  setNumRoomGuests,
  setRoomKind,
  setRoomDates,
  setSelectedExperiences,
  setNumSelectedExperiencesGuests,
  setExperiencesDates,
  setExperiencesTimes,
  setNotes,
  setHasExperience,
  setHasRoomKind,
  setStatus,
  setReservationType,
  setDateRanges,
  fieldChanged,
  focusOn,
  setNumTableGuests,
  setTableDate,
  setTableTime,
  setHasTable
} from "./actions";
import { getNumDays, lang } from "../model/utils";
import { getPrice } from "../model/price";
import countries from "../data/countries";

export default handleActions(
  {
    // field setters
    [combineActions(
      setName,
      setSurname,
      setCountry,
      setPostcode,
      setEmail,
      setPhoneNumber,
      setNumRoomGuests,
      setRoomKind,
      setRoomDates,
      setSelectedExperiences,
      setNumSelectedExperiencesGuests,
      setExperiencesDates,
      setExperiencesTimes,
      setNotes,
      setHasExperience,
      setHasRoomKind,
      setReservationType,
      setStatus,
      setDateRanges,
      setNumTableGuests,
      setTableDate,
      setTableTime,
      setHasTable
    )]: (state, { payload: { field, value } }) => {
      const newState = computeNewState(state, field, value);
      const dirtyState = computeDirtyStates(
        newState,
        field,
        state[field] !== value
      );
      // if (field === "experiencesDates" || field === "experiencesTimes" &&
      //   values(state[field]).join(",") !== values(newState[field]).join(",") &&
      //   state[field]) {
      // }
      return { ...newState, ...dirtyState };
    },

    // compute the room price
    [combineActions(setRoomKind, setRoomDates, setDateRanges)]: (
      state,
      { payload: { field, value } }
    ) => {
      const newState = computeNewState(state, field, value);
      const { roomKind, roomDates, dateRanges } = newState;
      return {
        ...newState,
        roomPrice:
          roomKind && dateRanges && roomDates
            ? getPrice(
                roomKind,
                dateRanges,
                moment(roomDates[0]),
                moment(roomDates[1])
              )
            : null,
        numNights: roomDates
          ? moment(roomDates[1]).diff(moment(roomDates[0]), "days")
          : undefined
      };
    },

    // compute the experience price
    [combineActions(setSelectedExperiences, setNumSelectedExperiencesGuests)]: (
      state,
      { payload: { field, value } }
    ) => {
      const updatedState = computeNewState(state, field, value);

      const defaults = (key, defaultValue) => ({
        [key]: fromPairs(
          value.map(experience => [
            experience.id,
            (updatedState[key] || {})[experience.id] || defaultValue
          ])
        )
      });

      const newState =
        field === "selectedExperiences"
          ? {
              ...updatedState,
              hasExperience: updatedState.hasExperience || value.length > 0,
              ...defaults("numSelectedExperiencesGuests", [1, 0]),
              ...defaults("experiencesDates", null),
              ...defaults("experiencesTimes", null)
            }
          : updatedState;

      const { selectedExperiences, numSelectedExperiencesGuests } = newState;

      const experiencesPrices = fromPairs(
        selectedExperiences.map(selectedExperience => {
          const numSelectedExperienceGuests =
            numSelectedExperiencesGuests[selectedExperience.id];
          return [
            selectedExperience.id,
            selectedExperience &&
            selectedExperience.price &&
            numSelectedExperienceGuests
              ? selectedExperience.price *
                (numSelectedExperienceGuests[0] +
                  numSelectedExperienceGuests[1])
              : null
          ];
        })
      );
      const prices = values(experiencesPrices);
      return {
        ...newState,
        experiencesPrices,
        experiencePrice: some(prices, price => price === null)
          ? null
          : sum(prices)
      };
    },

    // setting the experience also turns on the experience flag
    // (but having no experience doesn't mean we turn the flag off)
    [setSelectedExperiences]: (state, { payload: { value } }) => {
      return {
        ...state,
        hasExperience: state.hasExperience || value.length > 0
      };
    },

    // same for room
    [setRoomKind]: (state, { payload: { value } }) =>
      value ? { ...state, hasRoomKind: true } : state,

    // check whether the reservation is valid for rooms, and validate
    [combineActions(
      setName,
      setSurname,
      setCountry,
      setPostcode,
      setEmail,
      setPhoneNumber,
      setRoomKind,
      setRoomDates,
      setSelectedExperiences,
      setExperiencesDates,
      setExperiencesTimes
    )]: (state, { payload: { field, value } }) => {
      const newState = computeNewState(state, field, value);
      const errorStates = computeErrorStates(newState);
      const {
        name,
        surname,
        country,
        postcode,
        email,
        phoneNumber,
        roomKind,
        roomDates,
        experiencesDates,
        experiencesTimes
      } = newState;
      const rv = {
        ...newState,
        ...errorStates,
        roomReservationValid:
          !validateNotEmpty(name) &&
          !validateNotEmpty(surname) &&
          !validateNotDefault(country) &&
          !validateCap(postcode) &&
          !validateEmail(email) &&
          !validatePhoneNumber(phoneNumber) &&
          !validateExperiencesDates(experiencesDates) &&
          !validateExperiencesTimes(experiencesTimes) &&
          roomKind &&
          roomDates
            ? true
            : false
      };
      return rv;
    },

    // check whether the reservation is valid for experience
    [combineActions(
      setName,
      setSurname,
      setCountry,
      setPostcode,
      setEmail,
      setPhoneNumber,
      setSelectedExperiences,
      setExperiencesDates,
      setExperiencesTimes
    )]: (state, { payload: { field, value } }) => {
      const newState = computeNewState(state, field, value);
      const {
        name,
        surname,
        country,
        postcode,
        email,
        phoneNumber,
        selectedExperiences,
        experiencesDates,
        experiencesTimes
      } = newState;
      return {
        ...newState,
        experienceReservationValid:
          name &&
          surname &&
          country &&
          postcode &&
          email &&
          phoneNumber &&
          (selectedExperiences || []).length > 0 &&
          (experiencesDates || {})[selectedExperiences[0].id] &&
          (experiencesTimes || {})[selectedExperiences[0].id]
            ? true
            : false
      };
    },

    // check whether the reservation is valid for a table
    [combineActions(
      setName,
      setSurname,
      setEmail,
      setPhoneNumber,
      setNumTableGuests,
      setTableDate,
      setTableTime
    )]: (state, { payload: { field, value } }) => {
      const newState = computeNewState(state, field, value);
      const {
        name,
        surname,
        email,
        phoneNumber,
        numTableGuests,
        tableDate,
        tableTime
      } = newState;
      return {
        ...newState,
        tableReservationValid:
          name &&
          surname &&
          email &&
          phoneNumber &&
          numTableGuests &&
          numTableGuests >= 1 &&
          tableDate &&
          tableTime
            ? true
            : false
      };
    },

    // send analytics data on startup
    [setReservationType]: (state, { payload: { field, value } }) => {
      const newState = computeNewState(state, field, value);
      // window.tdcAnalytics.initialize("funnel", "tenute", 1, "funnel");
      window.tdcAnalytics.initFunnel({
        // eslint-disable-next-line babel/camelcase
        funnel_step: "start",
        // eslint-disable-next-line babel/camelcase
        funnel_type: getFunnelType(newState)
      });
      return newState;
    },

    // send analytics data on field interaction
    [fieldChanged]: (state, { payload: { fieldName } }) => {
      if (!state.compilationStarted) {
        window.tdcAnalytics.initFunnel({
          // eslint-disable-next-line babel/camelcase
          funnel_step: "inizio compilazione",
          // eslint-disable-next-line babel/camelcase
          funnel_type: getFunnelType(state)
        });
        window.tdcAnalytics.sendAction("funnelCompile", {
          // eslint-disable-next-line babel/camelcase
          action_name: "inizio compilazione",
          // eslint-disable-next-line babel/camelcase
          action_tech: "inizio compilazione",
          // eslint-disable-next-line babel/camelcase
          action_effect: getActionEffect(state),
          // eslint-disable-next-line babel/camelcase
          action_element_id: fieldName,
          // eslint-disable-next-line babel/camelcase
          action_type: "click",
          // eslint-disable-next-line babel/camelcase
          funnel_step: "inizio compilazione",
          // eslint-disable-next-line babel/camelcase
          funnel_type: getFunnelType(state)
        });
      }
      return { ...state, compilationStarted: true };
    },

    // send analytics data con mail sent
    [setStatus]: (status, { payload: { field, value } }) => {
      const s = computeNewState(status, field, value);
      if (s.status === "confirmed") {
        const obj = {
          ...{
            funnel_step: "end",
            funnel_type: getFunnelType(s)
          },
          // camere vedi documentazione (solo se la parte è compilata)
          ...(s.roomKind
            ? {
                room_name: s.roomKind.title,
                room_guests: s.numRoomGuests[0] + s.numRoomGuests[1],
                room_type: s.roomKind.title,
                room_check_in_day: formatAnalyticsDay(s.roomDates[0]),
                room_check_out_day: formatAnalyticsDay(s.roomDates[1]),
                room_booking_day: formatAnalyticsDay(new Date()),
                room_nights: getNumDays(s.roomDates) - 1,
                room_total_price: s.roomPrice
              }
            : {}),
          //Parte esperienze se presente vedi documento
          ...(s.selectedExperiences
            ? {
                experiences: s.selectedExperiences.map(e => ({
                  experience_name: e.title,
                  experience_guests:
                    s.numSelectedExperiencesGuests[e.id][0] +
                    s.numSelectedExperiencesGuests[e.id][1],
                  experience_type: e.type,
                  experience_day: formatAnalyticsDay(
                    s.experiencesDates[e.id]
                  ).format("DD-MM-YYYY"),
                  experience_total_price: e.price,
                  experience_booking_day: formatAnalyticsDay(new Date())
                }))
              }
            : {})
        };
        window.tdcAnalytics.initFunnel(obj);
        window.tdcAnalytics.sendAction("funnelEnd", obj);
      }
      return s;
    },

    [focusOn]: (state, { payload: { fieldName } }) => {
      console.log("focusing", fieldName);
      return {
        ...state,
        focusedField: fieldName
      };
    }
  },
  configuration.fakeData
    ? {
        // COMMON
        name: "Mattia",
        surname: "Belletti",
        email: "m@gmail.com",
        phoneNumber: "3404909675",
        nameDirty: true,
        surnameDirty: true,
        emailDirty: true,
        phoneNumberDirty: true,
        nameError: "",
        surnameError: "",
        emailError: "",
        phoneNumberError: "",
        focusedField: "country",
        status: "input",
        // COMPULSORY FIELDS
        numRoomGuests: [1, 0],
        hasRoomKind: false,
        hasExperience: false,
        hasTable: false,

        // IN ROOM / EXPERIENCES
        // country: "Italia",
        // postcode: "40139",
        // countryDirty: true,
        // postcodeDirty: true,
        // countryError: "",
        // postcodeError: "",
        // numRoomGuests: [1, 0],
        // hasExperience: true,
        // hasRoomKind: true,
        // experiencePrice: 450,
        // roomPrice: 200,
        // compilationStarted: true,
        // roomReservationValid: true,
        // experienceReservationValid: true,
        // reservationType: "room",
        // dateRanges: [
        //   {
        //     priceRange: "Bassa",
        //     dateFrom: moment("2024-04-27T22:00:00.000Z"),
        //     dateTo: moment("2024-06-13T22:00:00.000Z")
        //   },
        //   {
        //     priceRange: "Alta",
        //     dateFrom: moment("2024-06-14T22:00:00.000Z"),
        //     dateTo: moment("2024-08-01T22:00:00.000Z")
        //   },
        //   {
        //     priceRange: "Altissima",
        //     dateFrom: moment("2024-08-02T22:00:00.000Z"),
        //     dateTo: moment("2024-08-16T22:00:00.000Z")
        //   },
        //   {
        //     priceRange: "Alta",
        //     dateFrom: moment("2024-08-17T22:00:00.000Z"),
        //     dateTo: moment("2024-09-05T22:00:00.000Z")
        //   },
        //   {
        //     priceRange: "Media",
        //     dateFrom: moment("2024-09-06T22:00:00.000Z"),
        //     dateTo: moment("2024-09-19T22:00:00.000Z")
        //   },
        //   {
        //     priceRange: "Bassa",
        //     dateFrom: moment("2024-09-20T22:00:00.000Z"),
        //     dateTo: moment("2024-10-05T22:00:00.000Z")
        //   },
        //   {
        //     priceRange: "Alta",
        //     dateFrom: moment("2023-03-31T22:00:00.000Z"),
        //     dateTo: moment("2023-04-26T22:00:00.000Z")
        //   }
        // ],
        // roomKind: {
        //   id: "1",
        //   title: "Camera matrimoniale superior",
        //   lowPrice: 100,
        //   averagePrice: 200,
        //   highPrice: 300,
        //   veryHighPrice: 400,
        //   maxGuests: 2
        // },
        // roomDates: [
        //   new Date("2024-04-27T22:00:00.000Z"),
        //   new Date("2024-04-30T21:59:59.999Z")
        // ],

        // IN TABLE
        numTableGuests: 3,
        tableDate: new Date(),
        tableTime: [21, 15],
        numTableGuestsErrorDirty: false,
        tableDateDirty: false,
        tableTimeDirty: false,
        numTableGuestsError: "",
        tableDateError: "",
        tableTimeError: "",

        // IF IN EXPERIENCES
        // selectedExperiences: [
        // {
        //   id: 1,
        //   title: "Wine & Food Experience",
        //   description:
        //     "Visita guidata alla cantina e ala bottaia, pranzo-cena degustazione di 3 portate con 4 vini in abbinamento, presso il Relais “Villa Grazianella”, parte della tenuta.",
        //   duration: "120''",
        //   glasses: "5 calici",
        //   price: 45,
        //   wines: [
        //     "Nobile di Montepulciano Antica Chiusina",
        //     "Nobile di Montepulciano Riserva",
        //     "Nobile di Montepulciano",
        //     "Rosso di Montepulciano",
        //     "Manero Bianco"
        //   ],
        //   morningSlotStart: [9, 0],
        //   morningSlotEnd: [11, 15],
        //   afternoonSlotStart: [13, 0],
        //   afternoonSlotEnd: [21, 30]
        // },
        // {
        //   id: 2,
        //   title: "I vini di Monterufoli",
        //   description:
        //     "Visita alla cantina degustazione guidata di tutti i vini della tenuta in abbinamento a salumi e formaggi di produttori locali.",
        //   duration: "90''",
        //   glasses: "6 calici",
        //   price: 90,
        //   wines: ["Pian di Seta", "Vermentino di toscana", "Poggio Miniera"],
        //   morningSlotStart: [9, 0],
        //   morningSlotEnd: [11, 15],
        //   afternoonSlotStart: [13, 0],
        //   afternoonSlotEnd: [21, 30]
        // }
        // ],
        // numSelectedExperiencesGuests: {
        // "1": [2, 0],
        // "2": [1, 3]
        // },
        // experiencesDates: {
        // "1": null,
        // "2": null
        // },
        // experiencesTimes: {
        //   "1": null,
        //   "2": null
        // },
        // experiencesPrices: {
        //   "1": 90,
        //   "2": 360
        // },

        notes: "Prenotazione di test"
      }
    : {
        numRoomGuests: [1, 0],
        hasExperience: false,
        hasRoomKind: false,
        hasTable: false,
        experiencePrice: null,
        roomPrice: null,
        status: "input",
        compilationStarted: false,
        country: countries[lang].default,
        numTableGuests: 1
      }
);

const computeNewState = (state, field, value) => {
  // console.log(`setting ${field} to ${value} (now it is ${state[field]})`);
  return {
    ...state,
    [field]: value
  };
};

const computeDirtyStates = (newState, field, hasChanged) => {
  return fromPairs(
    [
      "name",
      "surname",
      "country",
      "postcode",
      "email",
      "phoneNumber",
      "experiencesDates",
      "experiencesTimes"
    ].map(fieldToCheck => [
      fieldToCheck + "Dirty",
      newState[fieldToCheck + "Dirty"] || (fieldToCheck === field && hasChanged)
    ])
  );
};

const computeErrorStates = ({
  name,
  nameDirty,
  surname,
  surnameDirty,
  country,
  countryDirty,
  postcode,
  postcodeDirty,
  email,
  emailDirty,
  phoneNumber,
  phoneNumberDirty,
  experiencesDates,
  experiencesTimes,
  experiencesDatesDirty,
  experiencesTimesDirty
}) => ({
  nameError: (nameDirty && validateNotEmpty(name)) || "",
  surnameError: (surnameDirty && validateNotEmpty(surname)) || "",
  countryError: (countryDirty && validateNotDefault(country)) || "",
  postcodeError: (postcodeDirty && validateCap(postcode)) || "",
  emailError: (emailDirty && validateEmail(email)) || "",
  phoneNumberError:
    (phoneNumberDirty && validatePhoneNumber(phoneNumber)) || "",
  experiencesDatesError:
    (experiencesDatesDirty && validateExperiencesDates(experiencesDates)) || "",
  experiencesTimesError:
    (experiencesTimesDirty && validateExperiencesTimes(experiencesTimes)) || ""
});

const getFunnelType = newState =>
  newState.reservationType === "room"
    ? "prenotazione soggiorno"
    : newState.reservationType === "table"
    ? "prenotazione tavolo"
    : "prenotazione esperienza";

const getActionEffect = newState =>
  newState.reservationType === "room"
    ? "inizio compilazione soggiorno"
    : newState.reservationType === "table"
    ? "inizio compilazione tavolo"
    : "inizio compilazione esperienza";
